// Security update: HTML rendered from stored content is sanitized with sanitizeHtml() before it is inserted into the page.
/* SimpleProductPageClient - Simple products use the same detail layout and purchase experience as variant products.
   Updated: Added DataFast product_viewed goal. */

import { sanitizeHtml } from '@/lib/sanitize-html';
import { useEffect, useState } from 'react';
import { marked } from 'marked';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/lib/supabase';
import { calculatePriceInclBTW } from '@/lib/btw-calculator';
import { getProductImageUrl } from '@/lib/image-url';
import ContactBar from '@/components/ContactBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBreadcrumb, { Crumb } from '@/components/PageBreadcrumb';
import ProductVariantDetails from '@/components/product/ProductVariantDetails';
import ProductSpecifications from '@/components/product/ProductSpecifications';
import ProductFAQ from '@/components/product/ProductFAQ';
import ProductReviews from '@/components/product/ProductReviews';
import FrequentlyBoughtTogether from '@/components/product/FrequentlyBoughtTogether';
import ServiceBenefits from '@/components/product/ServiceBenefits';
import OfferteCTA from '@/components/product/OfferteCTA';
import NotFoundPage from '@/pages/NotFoundPage';
import { ArrowRight, Loader as Loader2 } from 'lucide-react';
import { trackGoal } from '@/lib/datafast';

interface SimpleProduct {
  id: string;
  name: string;
  scientific_name: string;
  description: string;
  standplaats: string[];
  groeisnelheid: string;
  snoeien: string;
  groei_per_jaar: string;
  wintergroen: boolean;
  winterhard: boolean;
  maximale_hoogte: string;
  geslacht: string;
  base_slug: string;
  simple_price: number;
  btw_percentage: number;
  category_name: string;
  category_slug: string;
  subcategory_id: string | null;
  subcategory_name: string | null;
  subcategory_slug: string | null;
  stock_quantity: number;
  available_from: string | null;
}

interface ProductImage {
  id: string;
  storage_path: string;
  uses_new_bucket: boolean;
  url: string;
  display_order: number;
  is_primary: boolean;
}

interface ProductFAQItem {
  id: string;
  question: string;
  answer: string;
  display_order: number;
}

export default function SimpleProductPageClient() {
  const { category, product: productSlug } = useParams();
  const [product, setProduct] = useState<SimpleProduct | null>(null);
  const [images, setImages] = useState<ProductImage[]>([]);
  const [faqs, setFaqs] = useState<ProductFAQItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productSlug || !category) return;

      try {
        setLoading(true);
        setProduct(null);
        setImages([]);
        setFaqs([]);

        const { data: productData, error: productError } = await supabase
          .from('products')
          .select(`
            id, name, scientific_name, description, standplaats, groeisnelheid,
            snoeien, groei_per_jaar, wintergroen, winterhard, maximale_hoogte,
            geslacht, base_slug, simple_price, btw_percentage, available_from,
            subcategory_id, product_categories(name, slug)
          `)
          .eq('base_slug', `${category}/${productSlug}`)
          .eq('active', true)
          .eq('is_simple_product', true)
          .maybeSingle();

        if (productError) throw productError;
        if (!productData) return;

        let subcategoryName: string | null = null;
        let subcategorySlug: string | null = null;
        if (productData.subcategory_id) {
          const { data: subcategory } = await supabase
            .from('product_subcategories')
            .select('name, slug')
            .eq('id', productData.subcategory_id)
            .maybeSingle();
          subcategoryName = subcategory?.name || null;
          subcategorySlug = subcategory?.slug || null;
        }

        setProduct({
          id: productData.id,
          name: productData.name,
          scientific_name: productData.scientific_name || '',
          description: productData.description || '',
          standplaats: productData.standplaats || [],
          groeisnelheid: productData.groeisnelheid || '',
          snoeien: productData.snoeien || '',
          groei_per_jaar: productData.groei_per_jaar || '',
          wintergroen: productData.wintergroen ?? false,
          winterhard: productData.winterhard ?? false,
          maximale_hoogte: productData.maximale_hoogte || '',
          geslacht: productData.geslacht || '',
          base_slug: productData.base_slug,
          simple_price: parseFloat(productData.simple_price) || 0,
          btw_percentage: productData.btw_percentage || 9,
          category_name: (productData.product_categories as { name?: string } | null)?.name || '',
          category_slug: (productData.product_categories as { slug?: string } | null)?.slug || '',
          subcategory_id: productData.subcategory_id || null,
          subcategory_name: subcategoryName,
          subcategory_slug: subcategorySlug,
          stock_quantity: 999,
          available_from: productData.available_from || null,
        });

        const [{ data: imagesData }, { data: faqsData, error: faqsError }] = await Promise.all([
          supabase
            .from('product_images')
            .select('id, storage_path, uses_new_bucket, display_order, is_primary')
            .eq('product_id', productData.id)
            .is('variant_id', null)
            .order('display_order', { ascending: true }),
          supabase
            .from('product_faqs')
            .select('*')
            .eq('product_id', productData.id)
            .eq('active', true)
            .order('display_order'),
        ]);

        if (faqsError) throw faqsError;

        setImages((imagesData || []).map((image: Omit<ProductImage, 'url'>) => ({
          ...image,
          url: getProductImageUrl(image.storage_path, image.uses_new_bucket, 'large'),
        })));
        setFaqs(faqsData || []);
        trackGoal('product_viewed', { name: productData.name });
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productSlug, category]);

  if (loading) {
    return (
      <>
        <ContactBar />
        <div className="flex min-h-screen items-center justify-center bg-[#fcfaf6]">
          <Loader2 className="h-10 w-10 animate-spin text-[#1e2b22]" />
        </div>
      </>
    );
  }

  if (!product) return <NotFoundPage />;

  const priceInclBTW = calculatePriceInclBTW(product.simple_price, product.btw_percentage);
  const imageUrls = images.map((image) => image.url);
  const descriptionHtml = product.description
    ? sanitizeHtml(marked.parse(product.description) as string)
    : `<p>${product.name} is een sterke, veelzijdige plant voor een verzorgde tuin.</p>`;
  const pageTitle = `${product.name} kopen | Buitn Planten`;
  const pageDescription = `${product.name} kopen bij Buitn Planten. Topkwaliteit tuinproducten uit Dedemsvaart. Direct online bestellen.`;
  const pageImage = imageUrls[0] || 'https://buitnplanten.nl/assets/sections/hero.webp';
  const pageUrl = `https://buitnplanten.nl/${category}/${productSlug}`;
  const breadcrumbItems: Crumb[] = [
    { label: 'Home', href: '/' },
    { label: product.category_name, href: `/${product.category_slug}` },
    ...(product.subcategory_name && product.subcategory_slug
      ? [{ label: product.subcategory_name, href: `/${product.category_slug}/${product.subcategory_slug}` }]
      : []),
    { label: product.name },
  ];
  const simpleVariant = {
    id: product.id,
    size: product.name,
    type: 'Pakket',
    price: priceInclBTW.toString(),
    price_tier_1: priceInclBTW,
    stock_quantity: product.stock_quantity,
    sku: '',
    images: imageUrls,
    variantImages: imageUrls,
    genericImages: imageUrls,
    minimum_order_quantity: 1,
    available_from: product.available_from,
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="product:price:amount" content={priceInclBTW.toFixed(2)} />
        <meta property="product:price:currency" content="EUR" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: product.name,
            description: pageDescription,
            image: pageImage,
            brand: { '@type': 'Brand', name: 'Buitn Planten' },
            category: product.category_name,
            offers: {
              '@type': 'Offer',
              url: pageUrl,
              priceCurrency: 'EUR',
              price: priceInclBTW.toFixed(2),
              availability: 'https://schema.org/InStock',
              seller: { '@type': 'Organization', name: 'Buitn Planten' },
            },
          })}
        </script>
      </Helmet>

      <ContactBar />
      <div className="min-h-screen bg-[#fcfaf6]">
        <Header />
        <main className="mx-auto max-w-[1240px] px-5 pb-20 pt-8 lg:px-8">
          <PageBreadcrumb items={breadcrumbItems} />
          <ProductVariantDetails
            product={product}
            variant={simpleVariant}
            relatedVariants={[]}
            allVariants={[]}
            productSlug={product.base_slug}
            categorySlug={product.category_slug}
            isSimpleProduct
          />
          <ServiceBenefits />

          <div className="sticky top-[176px] z-10 mb-10 hidden overflow-x-auto border-y border-[#e7e1d6] bg-[#fcfaf6]/95 py-3 backdrop-blur lg:block">
            <nav className="flex min-w-max items-center justify-center gap-8 text-sm font-medium text-[#5a6552]" aria-label="Productinformatie">
              <a href="#omschrijving" className="tab-underline transition-colors hover:text-[#1e2b22]">Omschrijving</a>
              <a href="#specificaties" className="tab-underline transition-colors hover:text-[#1e2b22]">Specificaties</a>
              <a href="#reviews" className="tab-underline transition-colors hover:text-[#1e2b22]">Reviews</a>
              <a href="#veelgestelde-vragen" className="tab-underline transition-colors hover:text-[#1e2b22]">Veelgestelde vragen</a>
            </nav>
          </div>

          <section id="omschrijving" className="mb-16 scroll-mt-48">
            <div className="mb-8">
              <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">Meer over deze plant</p>
              <h2 className="text-3xl tracking-[-0.035em] text-black md:text-4xl">Omschrijving</h2>
            </div>
            <div className="prose prose-base max-w-none leading-7 text-[#1a1a1a]" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
          </section>

          <section id="specificaties" className="scroll-mt-48">
            <ProductSpecifications product={product} variant={simpleVariant} />
          </section>

          <section id="reviews" className="mb-16 scroll-mt-48">
            <div className="mb-6">
              <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">Wat klanten zeggen</p>
              <h2 className="text-3xl tracking-[-0.035em] text-black sm:text-4xl">Reviews</h2>
              <p className="mt-3 text-base leading-7 text-[#1a1a1a]">Ervaringen van klanten die met Buitn Planten aan de slag gingen.</p>
            </div>
            <ProductReviews />
          </section>

          {faqs.length > 0 && (
            <section id="veelgestelde-vragen" className="mb-16 scroll-mt-48">
              <div className="mb-6">
                <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">Vragen</p>
                <h2 className="text-3xl tracking-[-0.035em] text-black sm:text-4xl">Veelgestelde vragen</h2>
                <p className="mt-3 text-base leading-7 text-[#1a1a1a]">Antwoorden op de meest gestelde vragen over dit product.</p>
              </div>
              <ProductFAQ faqs={faqs} />
            </section>
          )}

          <section className="mb-16">
            <div className="mb-8">
              <p className="mb-3 text-xs uppercase tracking-[0.18em] text-[#5a6552]">Alternatieven</p>
              <h2 className="text-3xl tracking-[-0.035em] text-black md:text-4xl">Op zoek naar alternatieven?</h2>
              <p className="mt-3 text-base leading-7 text-[#1a1a1a]">Bekijk onze andere producten uit dezelfde categorie of ons volledige assortiment.</p>
            </div>
            <FrequentlyBoughtTogether currentProductId={product.id} subcategoryId={product.subcategory_id} categorySlug={product.category_slug} />
            <div className="mt-8 text-center">
              <a href="/assortiment" className="group inline-flex min-h-12 items-center justify-center gap-3 rounded-md bg-[#1e2b22] px-6 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(30,43,34,0.18)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#5a6552] sm:px-7">
                Naar assortiment
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </section>

          <OfferteCTA />
        </main>
        <Footer />
      </div>
    </>
  );
}
